import { FC, useState, useMemo } from "react";
import moment from "moment";
import { Table, Spin } from "antd";
import { ColumnsType } from "antd/es/table";
import { BackCircleIcon } from "src/components/icons/back-circle-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { SearchIcon } from "src/components/icons/search-icon";
import Button from "src/components/ui/button";
import ToggleSwitch from "src/components/ui/switch";
import { WhiteLabelAdminItem } from "src/api/types";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
} from "src/lib/utils";

interface WhiteLabelAdminDetailsProps {
  admin: WhiteLabelAdminItem | null;
  loading: boolean;
  channelPartnersMap: Record<number, string>;
  rolesMap: Record<number, string>;
  onBack: () => void;
  onEdit: (admin: WhiteLabelAdminItem) => void;
  onRefresh?: () => void;
  onToggleStatus?: (admin: WhiteLabelAdminItem, newStatus: boolean) => void | Promise<void>;
  updatingAdminId?: number | null;
  canEdit?: boolean;
  canBlock?: boolean;
}

const DetailItem: FC<{ title: string; value: React.ReactNode }> = ({
  title,
  value,
}) => (
  <div className="flex flex-col gap-1">
    <p className="text-secondary font-manrope text-[14px] font-medium">
      {title}:
    </p>
    <div className="text-primary font-manrope text-[14px] font-medium">
      {value ?? "__"}
    </div>
  </div>
);

const SUB_PERMISSION_CONFIG = [
  { key: "can_list", label: "List" },
  { key: "can_view", label: "View" },
  { key: "can_add", label: "Add" },
  { key: "can_edit", label: "Edit" },
  { key: "can_delete", label: "Delete" },
  { key: "can_approve_reject", label: "Approve / Reject" },
  { key: "can_export", label: "Export" },
  { key: "can_download", label: "Download" },
  { key: "can_block", label: "Block" },
];

interface ActionStatusItem {
  key: string;
  label: string;
  isGranted: boolean;
}

interface ParsedPermissionItem {
  id: number | string | null;
  name: string;
  allActions: ActionStatusItem[];
  grantedCount: number;
}

const WhiteLabelAdminDetails: FC<WhiteLabelAdminDetailsProps> = ({
  admin,
  loading,
  channelPartnersMap,
  rolesMap,
  onBack,
  onEdit,
  onRefresh,
  onToggleStatus,
  updatingAdminId,
  canEdit = true,
  canBlock = true,
}) => {
  const [permissionFilter, setPermissionFilter] = useState("");
  const [permCurrentPage, setPermCurrentPage] = useState(1);
  const permPageSize = 5;

  const channelPartnerName = useMemo(() => {
    if (!admin) return "__";
    return (
      admin.channel_partner?.name ||
      admin.channelPartner?.name ||
      channelPartnersMap[admin.channel_partner_id] ||
      (admin.channel_partner_id ? `CP #${admin.channel_partner_id}` : "__")
    );
  }, [admin, channelPartnersMap]);

  const roleName = useMemo(() => {
    if (!admin) return "__";
    return (
      admin.role?.name ||
      rolesMap[admin.role_id] ||
      (admin.role_id ? `Role #${admin.role_id}` : "__")
    );
  }, [admin, rolesMap]);

  const fullName = useMemo(() => {
    if (!admin) return "";
    return `${admin.first_name || ""} ${admin.last_name || ""}`.trim();
  }, [admin]);

  // Parse permissions and all sub-actions with granted status
  const parsedPermissions = useMemo<ParsedPermissionItem[]>(() => {
    if (!admin?.role?.permissions || !Array.isArray(admin.role.permissions)) return [];

    return admin.role.permissions.map((perm: any, idx: number) => {
      const name =
        perm.permission_name ||
        perm.name ||
        perm.module_name ||
        `Module ${perm.permission_id || idx + 1}`;
      const id = perm.permission_id || perm.id || null;

      // Extract sub-permissions
      const subsObj = perm.subPermissions || perm.sub_permissions || perm;
      const hasSubsObj = subsObj && typeof subsObj === "object";
      const arrayActions: string[] = Array.isArray(perm.actions)
        ? perm.actions.map((a: string) => String(a).toLowerCase().trim())
        : [];

      const allActions: ActionStatusItem[] = SUB_PERMISSION_CONFIG.map((sub) => {
        let isGranted = false;
        if (
          hasSubsObj &&
          (subsObj[sub.key] === true ||
            subsObj[sub.key] === 1 ||
            subsObj[sub.key] === "true")
        ) {
          isGranted = true;
        } else if (arrayActions.length > 0) {
          const rawKey = sub.key.replace(/^can_/, "").toLowerCase();
          const rawLabel = sub.label.toLowerCase();
          if (arrayActions.includes(rawKey) || arrayActions.includes(rawLabel)) {
            isGranted = true;
          }
        }
        return {
          key: sub.key,
          label: sub.label,
          isGranted,
        };
      });

      // Include any custom actions from actions array
      const standardKeys = SUB_PERMISSION_CONFIG.map((s) =>
        s.key.replace(/^can_/, "").toLowerCase()
      );
      const standardLabels = SUB_PERMISSION_CONFIG.map((s) => s.label.toLowerCase());

      arrayActions.forEach((act) => {
        if (!standardKeys.includes(act) && !standardLabels.includes(act)) {
          allActions.push({
            key: act,
            label: capitalizeFirstLetter(act),
            isGranted: true,
          });
        }
      });

      const grantedCount = allActions.filter((a) => a.isGranted).length;

      return {
        id,
        name,
        allActions,
        grantedCount,
      };
    });
  }, [admin?.role?.permissions]);

  // Filtered permissions based on user search
  const filteredPermissions = useMemo(() => {
    if (!permissionFilter.trim()) return parsedPermissions;
    const query = permissionFilter.toLowerCase().trim();
    return parsedPermissions.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        String(p.id).includes(query) ||
        p.allActions.some(
          (a) => a.isGranted && a.label.toLowerCase().includes(query)
        )
    );
  }, [parsedPermissions, permissionFilter]);

  // Permissions Table Columns without '#' before ID
  const permissionColumns: ColumnsType<ParsedPermissionItem> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      className: "w-[70px]",
      render: (val: any, _, idx: number) => (
        <p className="font-manrope text-primary text-[14px] font-medium">
          {val ?? idx + 1}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Permission Module
        </p>
      ),
      dataIndex: "name",
      key: "name",
      className: "w-[220px] min-w-[180px]",
      render: (val: string, record: ParsedPermissionItem) => (
        <div className="flex flex-col gap-0.5">
          <p className="font-manrope text-primary text-[14px] font-semibold">
            {capitalizeFirstLetter(val)}
          </p>
          <span className="font-manrope text-[11px] text-secondary">
            {record.grantedCount} / {record.allActions.length} actions granted
          </span>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Granted Actions
        </p>
      ),
      key: "actions",
      className: "w-auto",
      render: (_, record) => {
        return (
          <div className="flex flex-wrap gap-1.5 py-1">
            {record.allActions.map((action) => (
              <span
                key={action.key}
                className={`inline-flex items-center gap-1 text-[11px] font-manrope px-2 py-0.5 rounded-[6px] border transition-all select-none ${action.isGranted
                  ? "font-semibold bg-[#E6F9E6] text-[#228B22] border-[#B7EB8F] shadow-[0_1px_2px_rgba(34,139,34,0.06)]"
                  : "font-normal bg-[#F8F9FA] text-[#9CA3AF] border-[#E6E8EC]"
                  }`}
                title={`${action.label}: ${action.isGranted ? "Granted" : "Not Granted"}`}
              >
                {action.isGranted ? (
                  <svg
                    className="w-2.5 h-2.5 text-[#228B22] flex-shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3334 4L6.00008 11.3333L2.66675 8"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-2 h-2 text-[#9CA3AF] flex-shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                <span>{action.label}</span>
              </span>
            ))}
          </div>
        );
      },
    },
  ];

  if (loading && !admin) {
    return (
      <div className="w-full flex flex-col items-center justify-center min-h-[350px] bg-white rounded-[12px] p-6 mt-4">
        <Spin size="large" />
        <p className="font-manrope text-secondary text-[14px] mt-3 font-medium">
          Loading admin details...
        </p>
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="w-full flex flex-col items-center justify-center min-h-[250px] bg-white rounded-[12px] p-6 mt-4">
        <p className="font-manrope text-secondary text-[15px] font-medium">
          Admin not found or failed to load.
        </p>
        <button
          type="button"
          className="mt-4 px-4 py-2 bg-primary text-white rounded-[8px] text-[14px] font-manrope font-medium"
          onClick={onBack}
        >
          Back to Admin List
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      {/* Header with Back Button, Name, Status & Edit Button */}
      <div className="flex flex-row items-center justify-between sm:justify-start gap-4">
        <div className="flex flex-row items-center gap-2">
          <Button
            variant="icon"
            className="w-[24px] bg-transparent h-[24px]"
            onClick={onBack}
            title="Back"
          >
            <BackCircleIcon className="w-[24px] h-[24px] text-primary" />
          </Button>
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[38px] sm:leading-[48px]">
            {fullName ? capitalizeEachFirstLetter(fullName) : `Admin #${admin.id}`}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span
            className={`font-manrope text-[12px] font-medium px-2.5 py-0.5 rounded-full ${admin.is_active
              ? "bg-[#E6F9E6] text-[#228B22]"
              : "bg-[#FEEAEA] text-[#D32F2F]"
              }`}
          >
            {admin.is_active ? "Active" : "Inactive"}
          </span>

          {canEdit && (
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E6E8EC] hover:border-brand text-primary rounded-[8px] text-[13px] font-medium transition-colors ml-2"
              onClick={() => onEdit(admin)}
              title="Edit Admin"
            >
              <EditIcon className="w-[16px] h-[16px] text-primary" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* 1. Admin Information Card */}
      <div className="bg-white rounded-[12px] w-full p-6 mt-4 border border-light-300 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-8">
          <DetailItem title="Admin ID" value={admin.id} />
          <DetailItem title="First Name" value={admin.first_name || "__"} />
          <DetailItem title="Last Name" value={admin.last_name || "__"} />

          <DetailItem title="Email" value={admin.email || "__"} />
          <DetailItem
            title="Channel Partner"
            value={capitalizeFirstLetter(channelPartnerName)}
          />
          <DetailItem
            title="Role"
            value={capitalizeFirstLetter(roleName)}
          />

          <DetailItem
            title="Google 2FA"
            value={
              <span
                className={`font-manrope text-[12px] font-medium px-2 py-0.5 rounded-full ${admin.google2fa_enabled
                  ? "bg-[#E6F9E6] text-[#228B22]"
                  : "bg-[#F3F4F6] text-[#6B7280]"
                  }`}
              >
                {admin.google2fa_enabled ? "Enabled" : "Disabled"}
              </span>
            }
          />

          <DetailItem
            title="Different Channel Access"
            value={
              <span
                className={`font-manrope text-[12px] font-medium px-2 py-0.5 rounded-full ${admin.is_different_channel_access
                  ? "bg-[#E6F9E6] text-[#228B22]"
                  : "bg-[#F3F4F6] text-[#6B7280]"
                  }`}
              >
                {admin.is_different_channel_access ? "Yes" : "No"}
              </span>
            }
          />

          <DetailItem
            title="Status"
            value={
              <div className="flex items-center gap-2">
                <span
                  className={`font-manrope text-[12px] font-medium px-2.5 py-0.5 rounded-full ${admin.is_active
                    ? "bg-[#E6F9E6] text-[#228B22]"
                    : "bg-[#FEEAEA] text-[#D32F2F]"
                    }`}
                >
                  {admin.is_active ? "Active" : "Inactive"}
                </span>
                <div
                  className={!canBlock || updatingAdminId === admin.id ? "opacity-50 cursor-not-allowed" : ""}
                  title={!canBlock ? "Read only - No block permission" : undefined}
                >
                  <ToggleSwitch
                    type={`${admin.id}`}
                    value={!!admin.is_active}
                    disabled={!canBlock || updatingAdminId === admin.id}
                    onValueChanged={(_: any, newValue: boolean) => {
                      if (!canBlock) return;
                      onToggleStatus?.(admin, newValue);
                    }}
                  />
                </div>
              </div>
            }
          />

          <DetailItem
            title="Last Login"
            value={
              admin.last_login
                ? moment(admin.last_login).format("YYYY-MM-DD HH:mm:ss")
                : "__"
            }
          />

          <DetailItem
            title="Last Activity"
            value={
              admin.last_activity
                ? moment(admin.last_activity).format("YYYY-MM-DD HH:mm:ss")
                : "__"
            }
          />

          <DetailItem
            title="Created At"
            value={
              admin.created_at
                ? moment(admin.created_at).format("YYYY-MM-DD HH:mm:ss")
                : "__"
            }
          />

          <DetailItem
            title="Updated At"
            value={
              admin.updated_at
                ? moment(admin.updated_at).format("YYYY-MM-DD HH:mm:ss")
                : "__"
            }
          />
        </div>
      </div>

      {/* 2. Role Permissions Table Card */}
      <div className="bg-white rounded-[12px] w-full p-6 mt-4 border border-light-300 shadow-sm flex flex-col">
        {/* Header with Title, Stats & Search Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-light-300">
          <div className="flex items-center gap-3">
            <p className="font-manrope font-bold text-[18px] text-primary">
              Role Permissions
            </p>
            <span
              className={`text-[12px] font-manrope font-semibold px-2.5 py-0.5 rounded-full ${parsedPermissions.length > 0
                ? "bg-[#E6F9E6] text-[#228B22]"
                : "bg-[#FEEAEA] text-[#D32F2F]"
                }`}
            >
              {parsedPermissions.length > 0
                ? `${parsedPermissions.length} Modules Assigned`
                : "No Permissions"}
            </span>
          </div>

          {/* Search Filter if permissions exist */}
          {parsedPermissions.length > 0 && (
            <div className="relative w-full sm:w-[240px]">
              <input
                type="text"
                placeholder="Search permissions..."
                value={permissionFilter}
                onChange={(e) => {
                  setPermissionFilter(e.target.value);
                  setPermCurrentPage(1);
                }}
                className="w-full h-[38px] pl-8 pr-7 text-[13px] font-manrope rounded-[8px] border border-border bg-white text-primary focus:border-active focus:outline-none"
              />
              <SearchIcon className="w-4 h-4 text-secondary absolute left-2.5 top-1/2 -translate-y-1/2" />
              {permissionFilter && (
                <button
                  type="button"
                  onClick={() => {
                    setPermissionFilter("");
                    setPermCurrentPage(1);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-secondary hover:text-primary text-[14px] leading-none"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>

        {/* Content: Table or Highlighted Warning when no permissions */}
        {parsedPermissions.length > 0 ? (
          <div className="w-full mt-3">
            <Table
              columns={permissionColumns}
              dataSource={filteredPermissions}
              rowKey={(record, idx) => String(record.id || idx)}
              pagination={{
                current: permCurrentPage,
                pageSize: permPageSize,
                total: filteredPermissions.length,
                onChange: (page) => setPermCurrentPage(page),
                position: ["bottomCenter"],
                showSizeChanger: false,
              }}
            />
          </div>
        ) : (
          <div className="mt-4 p-5 rounded-[10px] bg-[#FFF8E6] border border-[#FFE082] flex flex-col items-center justify-center text-center">
            <p className="font-manrope text-[15px] font-bold text-[#B78103]">
              No permissions assigned to role "{capitalizeFirstLetter(roleName)}"
            </p>
            <p className="font-manrope text-[13px] text-secondary mt-1">
              This admin currently has no module access or action permissions configured under role "{capitalizeFirstLetter(roleName)}".
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhiteLabelAdminDetails;
